// Platformbrede constanten: namen, poorten en de i18n-namespaces uit §17.8.
// Alles wat meer dan één applicatie moet weten staat hier, zodat een poort of
// een namespace nergens twee keer wordt gespeld.

import { SOURCE_LOCALE, SUPPORTED_LOCALES, type Locale } from './locales'

/** Interne werknaam van het platform (§16); nooit zichtbaar in gastcontent. */
export const PLATFORM_NAME = 'siteshowroom'

/** npm-scope van alle interne packages. */
export const PACKAGE_SCOPE = '@siteshowroom'

/** De vier draaiende applicaties uit de monorepo. */
export const APP_NAMES = ['api', 'web-public', 'web-dashboard', 'web-admin'] as const

export type AppName = (typeof APP_NAMES)[number]

/** Packagenaam per applicatie, zoals in de workspace vastgelegd. */
export const APP_PACKAGE_NAMES = {
  api: `${PACKAGE_SCOPE}/api`,
  'web-public': `${PACKAGE_SCOPE}/web-public`,
  'web-dashboard': `${PACKAGE_SCOPE}/web-dashboard`,
  'web-admin': `${PACKAGE_SCOPE}/web-admin`,
} as const satisfies Record<AppName, string>

/** Poorten waarop de applicaties in ontwikkeling draaien. */
export const DEFAULT_PORTS = {
  api: 4311,
  'web-public': 4310,
  'web-dashboard': 4312,
  'web-admin': 4313,
} as const satisfies Record<AppName, number>

/** Basis-URL per applicatie in ontwikkeling; productie zet dit via de omgeving. */
export const DEFAULT_BASE_URLS = {
  api: `http://localhost:${DEFAULT_PORTS.api}`,
  'web-public': `http://localhost:${DEFAULT_PORTS['web-public']}`,
  'web-dashboard': `http://localhost:${DEFAULT_PORTS['web-dashboard']}`,
  'web-admin': `http://localhost:${DEFAULT_PORTS['web-admin']}`,
} as const satisfies Record<AppName, string>

/** PostgreSQL draait in Docker op 5433 om niet te botsen met een lokale server. */
export const DEFAULT_POSTGRES_PORT = 5433

/** mailpit: SMTP-poort en webinterface. */
export const DEFAULT_SMTP_HOST = 'localhost'
export const DEFAULT_SMTP_PORT = 1025

/**
 * Bezorgdrivers voor uitgaande post.
 *
 * `log` schrijft naar de serverlog en verstuurt niets. Dat is geen testmodus maar de
 * stand waarin dit platform maanden heeft gedraaid; hij heeft nu een naam zodat hij
 * een zichtbare keuze is in plaats van een ontbrekende voorziening (§17.31).
 *
 * `graph` verstuurt via Microsoft 365 (§17.46). Voor wie daar al een abonnement heeft
 * is dat het pad met de minste losse eindjes: geen tweede leverancier, en SPF en DKIM
 * zijn al ingericht omdat de gewone post er ook doorheen gaat.
 */
export const MAIL_DRIVERS = ['smtp', 'graph', 'log'] as const
export type MailDriverName = (typeof MAIL_DRIVERS)[number]

/** Lokaal gaat alles naar mailpit; die accepteert elke afzender. */
export const DEFAULT_MAIL_DRIVER: MailDriverName = 'smtp'
export const DEFAULT_MAIL_FROM = 'no-reply@siteshowroom.test'
export const DEFAULT_MAIL_FROM_NAME = 'SiteShowroom'

/**
 * Hoe vaak de berichtenwachtrij wordt geleegd, in minuten. 0 zet hem uit.
 *
 * Een minuut is fijnmazig genoeg: berichten met een vertrekpunt in de toekomst —
 * "drie dagen vóór aankomst" — worden op een tijdstip gepland en niet op een
 * seconde, en een bevestiging die een minuut later aankomt is geen bevestiging die
 * te laat is.
 */
export const DEFAULT_MESSAGE_QUEUE_MINUTES = 1

/** Hoe vaak een mislukt bericht opnieuw wordt geprobeerd voordat het blijft liggen. */
export const MAX_MESSAGE_ATTEMPTS = 5
export const DEFAULT_MAILPIT_UI_PORT = 8025

/** Standaardwaarden voor de overige omgevingsvariabelen. */
export const DEFAULT_DATABASE_POOL_MAX = 10
export const DEFAULT_STORAGE_LOCAL_ROOT = './.data/media'

/**
 * Hoe vaak kanaalfeeds worden opgehaald, in minuten; 0 zet de taak uit.
 *
 * Vijftien is de afspraak uit hoofdstuk 19 ("binnen enkele minuten") afgezet
 * tegen wat de kanalen zelf doen: Airbnb en Booking.com verversen hun feed ook
 * niet vaker. Korter ophalen levert geen versere gegevens op, alleen verkeer.
 */
export const DEFAULT_CHANNEL_IMPORT_MINUTES = 15

/** Het health-endpoint dat stage 0 moet opleveren. */
export const HEALTH_ENDPOINT_PATH = '/health'

/** Toegestane waarden van NODE_ENV. */
export const NODE_ENVS = ['development', 'test', 'production'] as const
export type NodeEnv = (typeof NODE_ENVS)[number]

/** Drivers van StorageService (§17.2); 'local' is actief, 's3' volgt bij groei. */
export const STORAGE_DRIVERS = ['local', 's3'] as const
export type StorageDriver = (typeof STORAGE_DRIVERS)[number]

/** Logniveaus, oplopend van meest naar minst ernstig. */
export const LOG_LEVELS = ['fatal', 'error', 'warn', 'info', 'debug', 'trace', 'silent'] as const
export type LogLevel = (typeof LOG_LEVELS)[number]

export const DEFAULT_LOG_LEVEL: LogLevel = 'info'

/** Vertaalnamespaces uit §17.8; één catalogusbestand per namespace per taal. */
export const I18N_NAMESPACES = ['common', 'guest', 'dashboard', 'admin', 'amenities'] as const

export type Namespace = (typeof I18N_NAMESPACES)[number]

/**
 * Welke talen per namespace verplicht aanwezig moeten zijn.
 *
 * De administrator backend wordt niet vertaald en gebruikt uitsluitend de
 * brontaal, maar loopt wél via dezelfde i18n-laag (§17.8). De CI-controle die
 * op ontbrekende sleutels faalt gebruikt deze tabel als waarheid.
 */
export const NAMESPACE_LOCALES: Record<Namespace, readonly Locale[]> = {
  common: SUPPORTED_LOCALES,
  guest: SUPPORTED_LOCALES,
  dashboard: SUPPORTED_LOCALES,
  admin: [SOURCE_LOCALE],
  amenities: SUPPORTED_LOCALES,
}

/**
 * Welke namespaces elke applicatie laadt.
 *
 * Dit bestond niet, en dat kostte een fout die tot bij de gebruiker kwam: het
 * beschrijvingsscherm gebruikte `guest.amenities.items.*`, die sleutel bestond
 * netjes in de catalogus, en de controle vond hem dus. Alleen laadt het dashboard de
 * namespace `guest` niet, dus in de browser was hij er niet.
 *
 * Een sleutel die bestaat is niet hetzelfde als een sleutel die beschikbaar is. Deze
 * tabel maakt dat verschil toetsbaar: de sleutelcontrole weet nu per bestand welke
 * namespaces de applicatie eromheen laadt, en faalt op een sleutel uit een namespace
 * die niet wordt geladen (§17.34).
 */
export const APP_NAMESPACES: Record<string, readonly Namespace[]> = {
  'apps/web-public': ['common', 'guest', 'amenities'],
  'apps/web-dashboard': ['common', 'dashboard', 'amenities'],
  'apps/web-admin': ['common', 'admin'],
}

/** Type guard voor een namespace uit onbetrouwbare invoer (bestandsnaam, CLI-argument). */
export function isNamespace(value: string): value is Namespace {
  return (I18N_NAMESPACES as readonly string[]).includes(value)
}

/** De talen die voor een namespace compleet moeten zijn. */
export function requiredLocalesFor(namespace: Namespace): readonly Locale[] {
  return NAMESPACE_LOCALES[namespace]
}
